import React, { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, ArrowLeft, CheckCircle2, CloudOff, Loader, RefreshCw, ShieldAlert } from 'lucide-react';
import Sheet from '../common/Sheet';
import { useSyncStatus } from '../../hooks/useSyncStatus';
import { Offline } from '../../services/offline/offlineServices';
import type { OutboxOp } from '../../services/offline/outbox';
import { acknowledgeHeld, describeCode, fixObservedTime, keepMine, keepServer, OP_LABELS, retryOp } from '../../services/offline/syncResolution';
import { requestChartReopen } from '../../services/patientmr/charting.service';
import { toClockTime, observedIsoFromTime } from '../../utils/observedAt';

export interface SyncCenterProps {
  online: boolean;
  onClose?: () => void;
}

const deps = () => ({ outbox: Offline.outbox, charts: Offline.charts, assessments: Offline.assessments });

const statusChip: Record<string, string> = {
  pending: 'bg-gray-100 text-gray-700',
  sending: 'bg-sky-100 text-sky-800',
  conflict: 'bg-red-100 text-red-800',
  needs_attention: 'bg-amber-100 text-amber-900',
};

const statusLabel = (op: OutboxOp) => {
  if (op.errorCode === 'QUARANTINED') return 'Held for review';
  if (op.status === 'needs_attention') return 'Needs attention';
  if (op.status === 'conflict') return 'Conflict';
  return op.attempts > 0 ? 'Retrying' : 'Waiting';
};

/** Flat "field: value" lines for the side-by-side (values are the clinician's own entries). */
function fieldsOf(value: unknown): Array<[string, string]> {
  if (value === null || value === undefined) return [];
  if (typeof value !== 'object') return [['value', String(value)]];
  return Object.entries(value as Record<string, unknown>)
    .filter(([, v]) => v !== undefined && v !== null && v !== '' && typeof v !== 'object')
    .map(([k, v]) => [k, String(v)]);
}

const Compare: React.FC<{ title: string; value: unknown }> = ({ title, value }) => (
  <div className="min-w-0 flex-1 rounded-lg border border-gray-200 p-2">
    <h4 className="mb-1 text-xs font-semibold uppercase text-gray-500">{title}</h4>
    <dl className="space-y-1 text-sm">
      {fieldsOf(value).map(([k, v]) => (
        <div key={k}><dt className="text-xs text-gray-500">{k}</dt><dd className="break-words text-gray-900">{v}</dd></div>
      ))}
      {fieldsOf(value).length === 0 && <dd className="text-gray-500">No details</dd>}
    </dl>
  </div>
);

/**
 * Sync Center (Phase 16C C8): what is still on this device, grouped by visit, with the status,
 * attempts and last error of each item; conflicts (mine vs the server's), items needing a fix
 * (observed time) and items held for supervisor review. Counts and the clinician's own work only.
 */
const SyncCenter: React.FC<SyncCenterProps> = ({ online, onClose }) => {
  const status = useSyncStatus();
  const [names, setNames] = useState<Record<string, string>>({});
  const [compare, setCompare] = useState<OutboxOp | null>(null);
  const [fixing, setFixing] = useState<OutboxOp | null>(null);
  const [fixTime, setFixTime] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const groups = useMemo(() => {
    const map = new Map<string, OutboxOp[]>();
    status.ops.forEach((op) => map.set(op.visitId, [...(map.get(op.visitId) || []), op]));
    return Array.from(map.entries());
  }, [status.ops]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const next: Record<string, string> = {};
      for (const [visitId] of groups) {
        const v = await Offline.visits.get(visitId).catch(() => undefined);
        next[visitId] = v?.patientName || 'Patient';
      }
      if (!cancelled) setNames(next);
    })();
    return () => { cancelled = true; };
  }, [groups]);

  const run = async (key: string, fn: () => Promise<unknown>, done?: string) => {
    setBusy(key);
    setMessage(null);
    try {
      await fn();
      if (done) setMessage(done);
      status.retry().catch(() => undefined);
    } catch (e) {
      setMessage(e instanceof Error ? e.message : 'That did not work. Try again.');
    } finally {
      setBusy(null);
    }
  };

  const blocked = status.engine.blocked;

  return (
    <div className="min-h-full bg-gray-50" data-testid="sync-center">
      <header className="sticky top-0 z-10 flex items-center gap-2 border-b border-gray-200 bg-white px-4 py-3">
        {onClose && (
          <button type="button" onClick={onClose} aria-label="Back" className="flex min-h-[44px] min-w-[44px] items-center justify-center rounded-lg border border-gray-200">
            <ArrowLeft size={18} />
          </button>
        )}
        <h1 className="flex-1 text-lg font-semibold text-gray-900">Sync Center</h1>
        <button type="button" onClick={() => status.retry()} disabled={!online || status.engine.syncing || !status.active} className="flex min-h-[44px] items-center gap-1 rounded-lg bg-slate-800 px-3 text-sm font-semibold text-white disabled:opacity-50">
          <RefreshCw size={16} className={status.engine.syncing ? 'animate-spin' : ''} aria-hidden="true" />{status.engine.syncing ? 'Syncing…' : 'Sync now'}
        </button>
      </header>

      <main className="mx-auto max-w-2xl space-y-3 px-4 py-4">
        {!online && <p className="flex items-center gap-2 rounded-lg border border-slate-300 bg-white p-3 text-sm text-slate-800"><CloudOff size={16} aria-hidden="true" />Offline. Everything below is saved encrypted on this device.</p>}
        {blocked === 'auth' && <p role="alert" className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">Sign in again to sync. Your work stays on this device.</p>}
        {blocked === 'account' && <p role="alert" className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">Your agency account is read-only. Work stays on this device until it is active again.</p>}
        {blocked === 'device' && <p role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">This device can't sync. Ask your administrator.</p>}
        {status.engine.lastError && !blocked && status.pending > 0 && online && <p className="text-sm text-gray-600">{status.engine.lastError}</p>}
        {message && <p role="status" className="rounded-lg bg-slate-100 p-3 text-sm text-slate-800">{message}</p>}

        {!status.active && <p className="rounded-xl border border-dashed border-gray-300 p-6 text-center text-sm text-gray-600">Unlock offline mode to see work kept on this device.</p>}
        {status.active && status.pending === 0 && (
          <p className="flex items-center justify-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 p-6 text-sm font-medium text-emerald-900"><CheckCircle2 size={18} aria-hidden="true" />Everything is synced.</p>
        )}

        {groups.map(([visitId, ops]) => (
          <section key={visitId} aria-label={`Visit for ${names[visitId] || 'patient'}`} className="rounded-xl border border-gray-200 bg-white">
            <h2 className="border-b border-gray-100 px-4 py-2 text-sm font-semibold text-gray-800">{names[visitId] || 'Patient'} <span className="font-normal text-gray-500">· {ops.length} item{ops.length === 1 ? '' : 's'}</span></h2>
            <ul className="divide-y divide-gray-100">
              {ops.map((op) => {
                const held = op.errorCode === 'QUARANTINED';
                const timeFixable = op.status === 'needs_attention' && !held && op.type.startsWith('CHART_') && op.type !== 'CHART_SUBMIT' && /^(TIME_|VALIDATION)/.test(op.errorCode || '');
                const submittedConflict = op.status === 'conflict' && op.errorCode === 'INVALID_TRANSITION';
                return (
                  <li key={op.opId} className="px-4 py-3 text-sm">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium text-gray-900">{OP_LABELS[op.type]}</span>
                      <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${statusChip[op.status] || statusChip.pending}`}>{statusLabel(op)}</span>
                      <span className="text-xs text-gray-500">{new Date(op.capturedAt).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}{op.attempts ? ` · ${op.attempts} attempt${op.attempts === 1 ? '' : 's'}` : ''}</span>
                    </div>
                    {(op.status === 'conflict' || op.status === 'needs_attention' || op.lastError) && (
                      <p className="mt-1 flex items-start gap-1 text-gray-700">
                        {held ? <ShieldAlert size={14} className="mt-0.5 shrink-0 text-amber-700" aria-hidden="true" /> : <AlertTriangle size={14} className="mt-0.5 shrink-0 text-amber-700" aria-hidden="true" />}
                        <span>{describeCode(op.errorCode)}{op.lastError && !held ? ` ${op.lastError}` : ''}</span>
                      </p>
                    )}
                    <div className="mt-2 flex flex-wrap gap-2">
                      {op.status === 'conflict' && !submittedConflict && (
                        <button type="button" onClick={() => setCompare(op)} className="min-h-[44px] rounded-lg border border-red-300 px-3 font-medium text-red-800">Compare and choose</button>
                      )}
                      {submittedConflict && (
                        <>
                          <button
                            type="button"
                            disabled={!online || busy === op.opId}
                            onClick={() => run(op.opId, () => requestChartReopen(op.patientId, op.visitId, 'Change made offline on a mobile device after the chart was submitted'), 'Addendum request sent to your supervisor.')}
                            className="min-h-[44px] rounded-lg border border-slate-400 px-3 font-medium text-slate-800 disabled:opacity-50"
                          >
                            Request addendum{online ? '' : ' (needs connection)'}
                          </button>
                          <button type="button" disabled={busy === op.opId} onClick={() => run(op.opId, () => keepServer(deps(), op), 'Your change was removed from this device.')} className="min-h-[44px] rounded-lg border border-gray-300 px-3 text-gray-700">Discard my change</button>
                        </>
                      )}
                      {timeFixable && (
                        <button type="button" onClick={() => { setFixing(op); setFixTime(op.observedAt ? toClockTime(Date.parse(op.observedAt)) : ''); }} className="min-h-[44px] rounded-lg border border-amber-400 px-3 font-medium text-amber-900">Fix observed time</button>
                      )}
                      {op.status === 'needs_attention' && !held && !timeFixable && (
                        <button type="button" disabled={busy === op.opId} onClick={() => run(op.opId, () => retryOp(deps(), op))} className="min-h-[44px] rounded-lg border border-gray-300 px-3 text-gray-700">Try again</button>
                      )}
                      {held && (
                        <button type="button" disabled={busy === op.opId} onClick={() => run(op.opId, () => acknowledgeHeld(deps(), op), 'Removed from this device. The office keeps the held copy.')} className="min-h-[44px] rounded-lg border border-gray-300 px-3 text-gray-700">OK, remove from this device</button>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          </section>
        ))}
        {status.engine.syncing && <p role="status" className="flex items-center gap-2 text-sm text-gray-600"><Loader size={14} className="animate-spin" />Syncing…</p>}
      </main>

      <Sheet open={Boolean(compare)} onClose={() => setCompare(null)} title="Choose a version" subtitle={compare ? OP_LABELS[compare.type] : ''}
        footer={compare && (
          <div className="flex gap-2">
            <button type="button" disabled={Boolean(busy)} onClick={() => { const op = compare; setCompare(null); run(op.opId, () => keepServer(deps(), op), 'Kept the version already saved.'); }} className="min-h-[48px] flex-1 rounded-lg border border-gray-300 text-base font-medium text-gray-800">Keep server's</button>
            <button type="button" disabled={Boolean(busy)} onClick={() => { const op = compare; setCompare(null); run(op.opId, () => keepMine(deps(), op), 'Your version will be sent.'); }} className="min-h-[48px] flex-1 rounded-lg bg-slate-800 text-base font-semibold text-white">Keep mine</button>
          </div>
        )}>
        {compare && (
          <div className="flex flex-col gap-2 sm:flex-row">
            <Compare title="Mine (this device)" value={(compare.payload as { data?: unknown }).data ?? compare.payload} />
            <Compare title="Server's" value={compare.current?.data ?? compare.current} />
          </div>
        )}
      </Sheet>

      <Sheet open={Boolean(fixing)} onClose={() => setFixing(null)} title="Fix observed time" subtitle={fixing ? OP_LABELS[fixing.type] : ''}
        footer={fixing && (
          <button
            type="button"
            disabled={!fixTime || Boolean(busy)}
            onClick={async () => {
              const op = fixing;
              const visit = await Offline.visits.get(op.visitId).catch(() => undefined);
              const iso = observedIsoFromTime(fixTime, { startedAt: visit?.startedAt, endedAt: visit?.endedAt });
              setFixing(null);
              if (iso) run(op.opId, () => fixObservedTime(deps(), op, iso), 'Corrected. It will be sent again.');
            }}
            className="min-h-[48px] w-full rounded-lg bg-slate-800 text-base font-semibold text-white disabled:opacity-50"
          >
            Save and resend
          </button>
        )}>
        <label htmlFor="sync-fix-time" className="mb-1 block text-sm font-medium text-gray-700">When did the care happen?</label>
        <input id="sync-fix-time" type="time" value={fixTime} onChange={(e) => setFixTime(e.target.value)} className="min-h-[44px] w-full rounded-lg border border-gray-300 px-3 text-base" />
      </Sheet>
    </div>
  );
};

export default SyncCenter;
